import React from "react";
import { HelmetProvider } from "react-helmet-async";
import { ThemeProvider } from "@/contexts/ThemeProvider";
import { Routes, Route } from "react-router-dom";
import LandingPage from "@/pages/LandingPage";
import "@/index.css";

// --- Code splitting: secondary pages load only when visited ---
const NotFoundPage = React.lazy(() => import("@/pages/NotFoundPage"));
const TermsPage = React.lazy(() => import("@/pages/TermsPage"));
const PrivacyPage = React.lazy(() => import("@/pages/PrivacyPage"));
const RiskDisclosurePage = React.lazy(
  () => import("@/pages/RiskDisclosurePage"),
);
const SecurityPage = React.lazy(() => import("@/pages/SecurityPage"));
const TradeLabPage = React.lazy(() => import("@/pages/TradeLabPage"));
const AboutPage = React.lazy(() => import("@/pages/AboutPage"));
const FaqPage = React.lazy(() => import("@/pages/FaqPage"));

// Error Boundary Component
class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean; error?: Error }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error("Pilum Finance App Error:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-background px-4">
          <div className="text-center p-8 max-w-md bg-card rounded-2xl shadow-2xl border border-primary/30">
            <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg
                className="w-8 h-8 text-primary"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"
                />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-text mb-2">
              Something went wrong
            </h1>
            <p className="text-text-secondary mb-6">
              We apologize for the inconvenience. Our team has been notified and
              is working to fix the issue.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={() => window.location.reload()}
                className="inline-flex items-center justify-center rounded-xl px-6 py-3 text-base font-semibold transition-all duration-300 ease-out focus:outline-none focus:ring-2 focus:ring-offset-2 bg-primary text-text shadow-lg hover:shadow-xl hover:bg-primary-hover hover:scale-105 focus:ring-primary transform border border-primary-hover"
              >
                Reload Page
              </button>
              <button
                onClick={() => this.setState({ hasError: false })}
                className="inline-flex items-center justify-center rounded-xl px-6 py-3 text-base font-semibold transition-all duration-300 ease-out focus:outline-none focus:ring-2 focus:ring-offset-2 bg-card text-text shadow-md ring-1 ring-border hover:bg-input hover:shadow-lg hover:scale-105 focus:ring-primary transform"
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Minimal fallback shown only while a lazy route chunk downloads
const RouteFallback: React.FC = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="w-10 h-10 rounded-full border-4 border-primary/20 border-t-primary animate-spin"></div>
  </div>
);

// Main App Component - renders immediately (no artificial loading delay)
const App: React.FC = () => {
  return (
    <ErrorBoundary>
      <HelmetProvider>
        <ThemeProvider>
          <div className="App">
            <React.Suspense fallback={<RouteFallback />}>
              <Routes>
                <Route path="/" element={<LandingPage />} />
                {/* --- New Routes Added Here --- */}
                <Route path="/terms" element={<TermsPage />} />
                <Route path="/privacy" element={<PrivacyPage />} />
                <Route
                  path="/risk-disclosure"
                  element={<RiskDisclosurePage />}
                />
                <Route path="/security" element={<SecurityPage />} />
                <Route path="/tradelab" element={<TradeLabPage />} />
                <Route path="/about" element={<AboutPage />} />
                <Route path="/faq" element={<FaqPage />} />
                {/* ----------------------------- */}
                <Route path="*" element={<NotFoundPage />} />
              </Routes>
            </React.Suspense>
          </div>
        </ThemeProvider>
      </HelmetProvider>
    </ErrorBoundary>
  );
};

export default App;
